import { useEffect, useState } from 'react';
import axiosInstance from '@/api/axiosInstance';
import { API_PREFIX } from '@/api/endpoints/api.constants';
import { appLocale, tr } from '@/i18n';
import type { CourseDetailsForUser } from '@/types/course.types';
import type { SeminarPreview } from '@/types/course-seminar.types';
import type { ApiTopic } from '@/components/admin/Courses/CourseBuilder/types/courseContentApi.types';

/* The tenant's public catalogue (GET /public/catalog) for the homepage blocks:
   live hero, category tiles and the course carousel share one request. Names
   are multilang strings ({en}…{en}{ar}…{ar}) — render them through ml(). */

export interface PublicCourse {
  id: number;
  /** A catalogue card: a course, or (catalogue page types) a program / certification / public community. */
  kind?: 'course' | 'program' | 'certification' | 'community';
  name: string;
  summary: string | null;
  category_id: number | null;
  duration_minutes: number | null;
  language: string | null;
  average_rating: number | null;
  cover_image_url: string | null;
  created_at: string | null;
  trend_score: number;
  level?: string | null;
  duration_weeks?: number | null;
  member_count?: number | null;
}

export interface PublicCategory {
  id: number;
  name: string;
  parent_id: number | null;
  depth: number;
  course_count: number;
  image_url: string | null;
}

export interface PublicCatalog {
  enabled: boolean;
  stats: { courses: number; programs: number; categories: number; learners: number; hours: number };
  categories: PublicCategory[];
  courses: PublicCourse[];
}

const requests = new Map<string, Promise<PublicCatalog | null>>();

export function fetchPublicCatalog(categoryId?: number | null): Promise<PublicCatalog | null> {
  const key = String(categoryId ?? '');
  let req = requests.get(key);
  if (!req) {
    req = axiosInstance
      .get(`${API_PREFIX}/public/catalog`, {
        params: categoryId ? { category_id: categoryId } : undefined,
        // Signed out there is no token to name the tenant; signed in (dashboard builder) the token wins.
        headers: { 'X-Tenant-ID': localStorage.getItem('tenant_id') || '1' },
        skipMultilangStrip: true,
      } as object)
      .then((res) => (res.data && typeof res.data === 'object' && Array.isArray(res.data.courses) ? (res.data as PublicCatalog) : null))
      .catch(() => {
        requests.delete(key);
        return null;
      });
    requests.set(key, req);
  }
  return req;
}

/** undefined while loading, null when the catalogue is off or unavailable (or `enabled` is false). */
export function usePublicCatalog(categoryId?: number | null, enabled = true): PublicCatalog | null | undefined {
  const [data, setData] = useState<PublicCatalog | null | undefined>(enabled ? undefined : null);
  useEffect(() => {
    if (!enabled) { setData(null); return undefined; }
    let alive = true;
    setData(undefined);
    fetchPublicCatalog(categoryId).then((d) => { if (alive) setData(d && d.enabled ? d : null); });
    return () => { alive = false; };
  }, [categoryId, enabled]);
  return data;
}

/** `category_counts` (with a search): matches per category, rolled up to parents — the chips count those. */
export interface PublicCoursePage { enabled: boolean; total: number; items: PublicCourse[]; category_counts?: Record<string, number> | null }
export type PublicSort = 'relevance' | 'newest' | 'popular' | 'title';
/** What the catalogue page lists: courses, live workshops (courses with a seminar), programs, public communities. */
export type PublicCatalogType = 'course' | 'seminar' | 'program' | 'community';
export const PUBLIC_CATALOG_TYPES: PublicCatalogType[] = ['course', 'seminar', 'program', 'community'];

const guestConfig = (params?: Record<string, unknown>) => ({
  params,
  headers: { 'X-Tenant-ID': localStorage.getItem('tenant_id') || '1' },
  skipMultilangStrip: true,
} as object);

/** Search the public catalogue (catalogue page, hero search suggestions). */
export async function searchPublicCourses(p: { q?: string; category_id?: number | null; sort?: PublicSort; limit?: number; offset?: number; type?: PublicCatalogType }): Promise<PublicCoursePage> {
  const params: Record<string, unknown> = { sort: p.sort ?? 'relevance', limit: p.limit ?? 24, offset: p.offset ?? 0 };
  if (p.type && p.type !== 'course') params.type = p.type;
  if (p.q?.trim()) params.q = p.q.trim();
  if (p.category_id) params.category_id = p.category_id;
  const res = await axiosInstance.get(`${API_PREFIX}/public/catalog/courses`, guestConfig(params));
  const d = res.data as PublicCoursePage;
  return d && Array.isArray(d.items) ? d : { enabled: false, total: 0, items: [] };
}

/* One cached search per sort + size, shared by everything on the page that needs it: the public header
   (is the catalogue public?) and the homepage's featured courses make a single small request together. */
const coursePages = new Map<string, Promise<PublicCoursePage | null>>();
function fetchCoursePage(sort: PublicSort, limit: number): Promise<PublicCoursePage | null> {
  const key = `${sort}:${limit}`;
  let req = coursePages.get(key);
  if (!req) {
    req = searchPublicCourses({ sort, limit }).catch(() => {
      coursePages.delete(key);
      return null;
    });
    coursePages.set(key, req);
  }
  return req;
}

/** A page of public courses (most popular first by default): undefined while loading, null when the
 *  catalogue isn't public (or unavailable). */
export function usePublicCourses(sort: PublicSort = 'popular', limit = 3): PublicCoursePage | null | undefined {
  const [data, setData] = useState<PublicCoursePage | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    fetchCoursePage(sort, limit).then((d) => { if (alive) setData(d && d.enabled ? d : null); });
    return () => { alive = false; };
  }, [sort, limit]);
  return data;
}

/** The learner course page's data for a visitor (no progress, no people); throws when the course isn't public. */
export async function fetchPublicCoursePage(id: number): Promise<CourseDetailsForUser> {
  const res = await axiosInstance.get(`${API_PREFIX}/public/catalog/courses/${id}/page`, guestConfig());
  return res.data as CourseDetailsForUser;
}

/** Modules and activities by title (no content) for the visitor's course page. */
export async function fetchPublicCourseOutline(id: number): Promise<ApiTopic[]> {
  const res = await axiosInstance.get(`${API_PREFIX}/public/catalog/courses/${id}/outline`, guestConfig());
  return Array.isArray(res.data) ? (res.data as ApiTopic[]) : [];
}

/** A free-preview SCORM package for a visitor: browse mode, nothing saved (the player's session cookie). */
export async function fetchPublicScormPreview(courseId: number, activityId: number): Promise<{ playerUrl: string; attempt?: number; mode?: string }> {
  const res = await axiosInstance.get(`${API_PREFIX}/public/catalog/courses/${courseId}/activities/${activityId}/scorm`, { ...guestConfig(), withCredentials: true } as object);
  return res.data;
}

/** The read-only Studio routes of a free-preview lesson for a visitor (StudioRuntime previewRoutes). */
/** A free-preview seminar for a visitor: events, sessions and seats taken (signing up needs an enrolment). */
export async function fetchPublicSeminarPreview(courseId: number, activityId: number): Promise<SeminarPreview> {
  const res = await axiosInstance.get(`${API_PREFIX}/public/catalog/courses/${courseId}/activities/${activityId}/seminar`, guestConfig());
  return res.data as SeminarPreview;
}

export const publicStudioPreviewRoutes = (courseId: number, activityId: number) => ({
  host: `/v1/public/catalog/courses/${courseId}/activities/${activityId}/studio/host`,
  api: `/public/catalog/courses/${courseId}/activities/${activityId}/studio/api`,
  anonymous: true,
});

/** A visitor's "Enrol" (or a click on an activity): sign in, then the course opens and enrols them (useEnrolIntent). */
export const signInToEnrolState = (courseId: number | string) => ({
  from: { pathname: `/user/course-start/${courseId}`, search: '?enrol=1', hash: '' },
});

/** Public page of a course (details + outline); enrolling there asks the visitor to sign in. */
export const publicCourseUrl = (id: number) => `/catalog/course/${id}`;

/* ── Cross-block events: a category picked in the hero search or on a tile filters the course carousel. ── */
export const CATEGORY_EVENT = 'landing:category';
export function pickCategory(id: number | null) {
  window.dispatchEvent(new CustomEvent<number | null>(CATEGORY_EVENT, { detail: id }));
}

/* ── Presentation helpers ─────────────────────────────────────────────── */

export function formatDuration(minutes: number | null): string {
  if (!minutes) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return tr('landing.duration_m', '{m} min', { m });
  if (!m) return tr('landing.duration_h', '{h} h', { h });
  return tr('landing.duration_h_m', '{h} h {m} min', { h, m });
}

export function languageName(code: string | null): string {
  if (!code) return '';
  // Older courses store the English word ("english") instead of the code.
  const tag = ({ english: 'en', arabic: 'ar', russian: 'ru' } as Record<string, string>)[code.toLowerCase()] ?? code;
  try {
    const name = new Intl.DisplayNames([appLocale()], { type: 'language' }).of(tag) ?? tag;
    return name.charAt(0).toLocaleUpperCase(appLocale()) + name.slice(1);
  } catch {
    return code.toUpperCase();
  }
}

/** The tenant's default cover is the same picture on every card — those cards get generated art instead. */
export const hasOwnCover = (c: PublicCourse) => !!c.cover_image_url && !/\/branding\/asset\//.test(c.cover_image_url);

export const TINTS = ['violet', 'sky', 'mint', 'sun', 'coral', 'pink'] as const;
export type Tint = typeof TINTS[number];
export const tintFor = (seed: number): Tint => TINTS[Math.abs(seed) % TINTS.length];

export const isNewCourse = (c: PublicCourse) =>
  !!c.created_at && Date.now() - new Date(c.created_at).getTime() < 30 * 24 * 3600 * 1000;

/** Lucide icon name for a category, from words in its (English) name. */
const ICON_WORDS: Array<[RegExp, string]> = [
  [/\b(ai|artificial|machine|prompt|gpt|data science)\b/i, 'Sparkles'],
  [/tech|software|it\b|program|cod|develop|cloud|digital/i, 'Cpu'],
  [/cyber|secur|privacy|risk/i, 'ShieldCheck'],
  [/data|analytic|report|insight/i, 'BarChart3'],
  [/business|management|strategy|operation/i, 'Briefcase'],
  [/lead|executive|coach/i, 'Compass'],
  [/\bhr\b|human|people|talent|recruit/i, 'Users'],
  [/design|creative|ux|ui\b|art/i, 'Palette'],
  [/financ|account|bank|econom/i, 'Landmark'],
  [/market|brand|communicat|media/i, 'Megaphone'],
  [/sales|customer|service/i, 'TrendingUp'],
  [/health|safety|wellbeing|medical/i, 'HeartPulse'],
  [/language|english|arabic|russian/i, 'Languages'],
  [/law|legal|compliance|policy|regulat/i, 'Scale'],
  [/engineer|industr|manufactur/i, 'Wrench'],
  [/onboard|induction|welcome/i, 'DoorOpen'],
];
export function categoryIcon(englishName: string): string {
  return ICON_WORDS.find(([re]) => re.test(englishName))?.[1] ?? 'BookOpen';
}

/** Largest tile count ≤ n that fills every row (4, 3, 2 or ≤6 in one row) — never a short last row. */
export function evenCount(n: number): number {
  if (n <= 6) return n;
  for (let m = n; m > 6; m -= 1) if (m % 4 === 0 || m % 3 === 0) return m;
  return 6;
}
